/**
 * Subscription Store - Zustand State Management
 * Connects to Stripe API via backend endpoints (/api/stripe/*)
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type SubscriptionPlan = "FREE" | "PLUS" | "PREMIUM";

export type SubscriptionType = "none" | "subscription" | "single_trip";

export type SubscriptionStatus =
  | "none"
  | "trialing"
  | "active"
  | "canceled"
  | "past_due"
  | "expired";

export interface UserSubscription {
  tier: SubscriptionPlan;
  type: SubscriptionType;
  status: SubscriptionStatus;
  currentPeriodEnd: string | null;
  singleTripExpiresAt: string | null;
  daysRemaining: number | null;
  hasStripeCustomer: boolean;
}

interface SubscriptionState {
  currentSubscription: UserSubscription | null;
  isLoading: boolean;
  error: string | null;
}

interface SubscriptionActions {
  fetchSubscription: (token: string) => Promise<void>;
  clearSubscription: () => void;
  clearError: () => void;
  getCurrentTier: () => SubscriptionPlan;
  hasAccess: (requiredPlan: SubscriptionPlan) => boolean;
}

type SubscriptionStore = SubscriptionState & SubscriptionActions;

const PLAN_HIERARCHY: Record<SubscriptionPlan, number> = {
  FREE: 0,
  PLUS: 1,
  PREMIUM: 2,
};

export const useSubscriptionStore = create<SubscriptionStore>()(
  persist(
    (set, get) => ({
      currentSubscription: null,
      isLoading: false,
      error: null,

      fetchSubscription: async (token: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await fetch("/api/stripe/my-subscription", {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (!response.ok) {
            if (response.status === 401) {
              set({ currentSubscription: null, isLoading: false });
              return;
            }
            throw new Error("Failed to fetch subscription");
          }

          const data = await response.json();
          if (data.success) {
            set({ currentSubscription: data.data, isLoading: false });
          } else {
            throw new Error(data.error?.message || "Unknown error");
          }
        } catch (error: any) {
          set({ error: error.message, isLoading: false });
        }
      },

      clearSubscription: () => {
        set({ currentSubscription: null, error: null });
      },

      clearError: () => {
        set({ error: null });
      },

      getCurrentTier: (): SubscriptionPlan => {
        const sub = get().currentSubscription;
        if (!sub) return "FREE";
        if (sub.status === "expired" || sub.status === "canceled")
          return "FREE";
        return (sub.tier as SubscriptionPlan) || "FREE";
      },

      hasAccess: (requiredPlan: SubscriptionPlan): boolean => {
        const currentTier = get().getCurrentTier();
        return PLAN_HIERARCHY[currentTier] >= PLAN_HIERARCHY[requiredPlan];
      },
    }),
    {
      name: "healthpass-subscription",
      partialize: (state) => ({
        currentSubscription: state.currentSubscription,
      }),
    },
  ),
);
